<template>
  <div>
    <tk-text-box
      :theme="theme"
      :placeholder="placeholder"
      :value="destinationName"
      :size="size"
      :icon="icon"
      :put-icon-outside="putIconOutside"
      :icon-color="iconColor"
      :icon-separator="iconSeparator"
      cursor="pointer"
      :disabled="disabled"
      readonly
      @click="openModal"
    />
    <tk-general-purpose-modal
      v-if="isModalOpened"
      no-show-header
      class="destinationPicker_modal"
      @close="isModalOpened = false"
    >
      <div class="destinationPicker_modalHeader">
        目的地を選択
        <span
          class="destinationPicker_modalHeaderCrossMarkArea"
          @click="closeModal"
        >
          <tk-cross-mark />
        </span>
      </div>
      <tk-text-box
        class="destinationPicker_autocomplete"
        :placeholder="'目的地を入力（例：ローマ）'"
        cursor="pointer"
        :size="'x-large'"
        :value="keyword"
        :autocomplete-items="autocompleteItems"
        @input="executeAutocomplete"
        @autocomplete="selectAutocompleteItem"
        @compositionstart="keywordComposing = true"
        @compositionend="keywordComposing = false"
      />
      <tk-tabs
        v-if="autocompleteItems.length === 0"
        nav-tabs-class="destinationPicker_tabs"
        active-class="active"
        content-class="tab-content"
        :active-tab-index="activeTabIndex"
      >
        <tk-tab title="人気都市">
          <!-- ref は jest 用 -->
          <tk-selectable-list
            v-if="selectablePopularAreas.length > 0"
            ref="popularCityList"
            :items="selectablePopularAreas"
            @selected="selectPopularArea"
          />
          <p
            v-else
            ref="noPopularAreas"
            class="destinationPicker_noDestinations"
          >
            選択可能な人気都市はありません
          </p>
        </tk-tab>
        <tk-tab title="エリアから選択">
          <ul class="destinationPicker_goBackLevelArea">
            <li class="destinationPicker_goBackLevelItem">
              <div
                v-if="!!parentDestination"
                class="destinationPicker_goBackIconTouchableArea"
                @click="widenLevel"
              >
                <tk-left-arrow class="destinationPicker_goBackLevelArrow" />
              </div>
              <span class="destinationPicker_goBackLevelLabel">{{ header }}</span>
            </li>
          </ul>
          <tk-selectable-list
            v-if="selectableAreas.length > 0"
            ref="areaList"
            :items="selectableAreas"
            @selected="selectArea"
          />
          <!-- ref は jest 用 -->
          <p
            v-else
            ref="noDestinations"
            class="destinationPicker_noDestinations"
          >
            選択可能な目的地がありません
          </p>
        </tk-tab>
      </tk-tabs>
    </tk-general-purpose-modal>
  </div>
</template>

<script>
import uniqBy from 'lodash/uniqBy'
import { asNumber, debounce } from '@ota-frontend/core/utils'
import { TkCrossMark, TkGeneralPurposeModal, TkLeftArrow, TkSelectableList, TkTab, TkTabs, TkTextBox } from '@ota-frontend/ui'
import { fetchBff } from '@/api'
import { Areas, Facets, SearchCondition } from '@/models'

export default {
  name: 'DestinationPicker',

  components: { TkCrossMark, TkGeneralPurposeModal, TkLeftArrow, TkSelectableList, TkTab, TkTabs, TkTextBox },

  model: {
    prop: 'condition',
    event: 'selected',
  },

  props: {
    condition: {
      type: SearchCondition,
      required: true,
    },
    // 検索条件として選択中の目的地名
    // これは condition 自身が知っていればベストだが...
    destinationName: {
      type: String,
      default: '',
    },
    // 全エリア情報（親子関係を認識するために使う）
    areas: {
      type: Areas,
      required: true,
    },
    // 選択可能な目的地情報を持つ Facets
    facets: {
      type: Facets,
      required: true,
    },
    theme: {
      type: String,
      default: 'default',
    },
    size: {
      type: String,
      default: 'normal',  // normal, large, x-large, xx-large, xxx-large
    },
    placeholder: {
      type: String,
      default: '目的地を選択',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'place',
    },
    putIconOutside: {
      type: Boolean,
      default: false,
    },
    iconColor: {
      type: String,
      default: '',
    },
    iconSeparator: {
      type: String,
      default: '',
    },
    activeTab: {
      // 目的地の初期選択タブ popular-city, area が選択可能
      type: String,
      default: undefined,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
      isModalOpened: false,
      selectingDestinationIndex: 0,
      prevSelectingDestinationIndex: 0,
      parentDestination: null,  // 現在選択中のエリアを 'region:13' のようにフォーマットしたもの。region:13 の場合はその配下の県のリストが表示されている。
      keyword: '',
      keywordComposing: false,
      autocompleteItems: [],
  }),

  computed: {
    header() {
      if (!this.parentDestination) return 'エリアをお選びください'

      const [level] = this.parentDestination.split(':')
      switch (level) {
        case 'region': return this.condition.isDomestic ? '県をお選びください' : '国をお選びください'
        case 'country': return '都市をお選びください'
        case 'city': return '都市をお選びください'  // 都市で良いのか？
        default: return ''
      }
    },
    selectedDestinationIds() {
    return this.condition.destinations
      .map((dest, index) => {
        if (index === this.selectingDestinationIndex) return null // skip current one
        if (dest.districtId) return `district:${dest.districtId}`
        if (dest.cityId) return `city:${dest.cityId}`
        if (dest.countryId) return `country:${dest.countryId}`
        if (dest.regionId) return `region:${dest.regionId}`
        return null
      })
      .filter(Boolean)
  },
    selectablePopularAreas() {
      const allPopulars = [
        ...this.facets.popularRegions.map(a => ({ id: `region:${a.code}`, name: a.name })),
        ...this.facets.popularCountries.map(a => ({ id: `country:${a.code}`, name: a.name })),
        ...this.facets.popularCities.map(a => ({ id: `city:${a.code}`, name: a.name })),
        ...this.facets.popularDistricts.map(a => ({ id: `district:${a.code}`, name: a.name })),
      ]

      return allPopulars.filter(item => !this.selectedDestinationIds.includes(item.id))
    },
    // 現在までに選択されている詳細度のエリア (parentDestination) に応じて次に選ぶべきエリアのリストを決定する
    selectableAreas() {
      // まだ何も選んでいないので、リージョンを選ぶところから
      const filterOutSelected = (items) =>
        items.filter(item => !this.selectedDestinationIds.includes(item.id))

      if (!this.parentDestination) {
        return filterOutSelected(this.selectableRegions.map(f => ({
          id: `region:${f.code}`,
          name: f.name,
        })))
      }

      const [level, code] = this.parentDestination.split(':')
      const id = Number(code)
      switch (level) {
        case 'region':
          // リージョンまで選択中 (配下の国・県をリストする)
          return filterOutSelected([
            // 国・県を選ばずにリージョンまでの選択とするための特別な項目
            {
              id: `whole-region:${id}`,
              name: '地域全域',
            },
            ...this.selectableCountriesUnder(id)
              .map(f => ({ id: `country:${f.code}`, name: f.name })),
          ])
        case 'country':
          // 国・県まで選択中 (配下の都市をリストする)
          return filterOutSelected([
            // 都市を選ばずに国・県までの選択とするための特別な項目
            {
              id: `whole-country:${id}`,
              name: this.condition.isDomestic ? '県全域' : '国全域',
            },
            ...this.selectableCitiesUnder(id)
              .map(f => ({ id: `city:${f.code}`, name: f.name })),
          ])
        case 'city':
          // 都市まで選択中 (配下の地区をリストする)
          return filterOutSelected([
            // 地区を選ばずに都市までの選択とするための特別な項目
            {
              id: `whole-city:${id}`,
              name: 'エリア全域',
            },
            ...this.selectableDistrictsUnder(id)
              .map(f => ({ id: `district:${f.code}`, name: f.name })),
          ])
        // 考慮外。ただの lint エラー回避
        default: return []
      }
    },
    selectableRegions() {
      return uniqBy(this.facets.regions, 'code')
    },
    activeTabIndex() {
      switch (this.activeTab) {
        case 'popular-city': return 0
        case 'area': return 1
        default: return this.destinationName ? 1 : 0
      }
    },
  },

  methods: {
    fetchAutocompleteItems: debounce(async function(keyword) {
      if (this.keywordComposing) return

      if (!keyword) {
        this.autocompleteItems = []
        return
      }

      const requestedKeyword = keyword
      const result = await fetchBff('tour/autocomplete-destination', {
        keyword,
        domestic: this.condition.isDomestic ? '1': '0',
      })
      // await 後に keyword が変化することがあるため、その場合はこの関数自体をキャンセルする
      if (this.keyword !== requestedKeyword) return

      const validItems = result.items.filter(({ type, code }) => {
        const id = Number(code)
        switch (type) {
          case 'region': return this.areas.existsRegion(id)
          case 'country': return this.areas.existsCountry(id)
          case 'city': return this.areas.existsCity(id)
          case 'district': return this.areas.existsDistrict(id)
          default: return false
        }
      })
      if (validItems.length === 0) {
        this.autocompleteItems = [
          {
            label: '条件にあう候補が見つかりませんでした',
            value: null,
          },
        ]
        return
      }

      this.autocompleteItems = uniqBy(validItems, 'code')
        .filter(item => item.type !== 'country' || item.name.includes(keyword))
        .map(item => {
          const parentLabel = item.type !== 'country' && item.parent ? `(${item.parent})` : ''
          return {
            label: `${item.name}${parentLabel}`,
            value: `${item.type}:${item.code}`,
          }
        })
    }, 300),
    executeAutocomplete(keyword) {
      this.keyword = keyword
      this.fetchAutocompleteItems(keyword)
    },
    openModal() {
      this.selectingDestinationIndex = this.index;
      // 最初にリストするべき内容を決めるため、現在の検索条件をもとに「選択中」とするエリアを決定する
      // district > city > country > region の優先順で、
      // 検索条件として指定されており、なおかつ件数 0 以上のもの (facets 内に存在するもの) を採用する
      this.parentDestination = (d => {
        if (this.isSelectable('district', d.districtId)) return `city:${this.areas.findDistrictById(d.districtId).city_id}`
        if (this.isSelectable('city', d.cityId)) return `country:${this.areas.findCityById(d.cityId).country_id}`
        if (this.isSelectable('country', d.countryId)) return `country:${d.countryId}`
        if (this.isSelectable('region', d.regionId)) return `region:${d.regionId}`
        return null  // リージョンすら指定していない完全な未選択状態
      })(this.condition.destinations[this.prevSelectingDestinationIndex])

      this.isModalOpened = true
    },
    isSelectable(level, id) {
      if (!id) return false

      const facets = (() => {
        switch(level) {
          case 'region': return this.facets.regions
          case 'country': return this.facets.countries
          case 'city': return this.facets.cities
          case 'district': return this.facets.districts
        }
      })()
      return facets.some(f => f.code === String(id))
    },
    widenLevel() {
      // 選択中の目的地の詳細度を下げる（１段広い選択に戻る）
      const [level, code] = this.parentDestination.split(':')
      switch (level) {
        case 'region':
          this.parentDestination = null
          break
        case 'country': {
          const country = this.areas.findCountryById(Number(code))
          this.parentDestination = `region:${country && country.region_id}`
          break
        }
        case 'city': {
          const city = this.areas.findCityById(Number(code))
          this.parentDestination = `country:${city && city.country_id}`
          break
        }
      }
    },
    selectPopularArea(optionId) {
      this.update(optionId)
      this.closeModal()
    },
    selectArea(optionId) {
      const [level, code] = optionId.split(':')
      const id = Number(code)

      // ～～全域の選択肢を選んだ場合は下層に潜らず確定させる
      if (level.startsWith('whole')) {
        switch (level) {
          case 'whole-region':
            this.updateRegion(id)
            break
          case 'whole-country':
            this.updateCountry(id)
            break
          case 'whole-city':
            this.updateCity(id)
            break
        }
        this.prevSelectingDestinationIndex = this.index;
        this.closeModal()
        return
      }

      switch (level) {
        case 'region':
          if (this.selectableCountriesUnder(id).length === 0) {
            // このリージョン配下には選択可能な国・県がない
            this.updateRegion(id)
            this.closeModal()
          } else {
            // 選択中のエリアを更新
            this.parentDestination = optionId
          }
          break
        case 'country':
          if (this.selectableCitiesUnder(id).length === 0) {
            // この国・県配下には選択可能な都市がない
            this.updateCountry(id)
            this.closeModal()
          } else {
            // 選択中のエリアを更新
            this.parentDestination = optionId
          }
          break
        case 'city':
          if (this.selectableDistrictsUnder(id).length === 0) {
            // この都市配下には選択可能な地区がない
            this.updateCity(id)
            this.closeModal()
          } else {
            // 選択中のエリアを更新
            this.parentDestination = optionId
          }
          break
        case 'district':
          // 最も詳細な地区まで選んだ
          this.updateDistrict(id)
          this.closeModal()
          break
      }
    },

    // 指定された ID のエリアの配下のエリアに対応する facet の配列を返す
    // ID は文字列を想定している
    selectableCountriesUnder(regionId) {
      return uniqBy(
        this.areas.filterByRegionId(regionId)
          .onlyCountriesOrMoreDetailed
          .areas
          .map(a => this.facets.countries.find(c => c.code === String(a.country_id)))
          .filter(Boolean),
        'code',
      )
    },
    selectableCitiesUnder(countryId) {
      return uniqBy(
        this.areas.filterByCountryId(countryId)
          .onlyCitiesOrMoreDetailed
          .areas
          .map(a => this.facets.cities.find(c => c.code === String(a.city_id)))
          .filter(Boolean),
        'code',
      )
    },
    selectableDistrictsUnder(cityId) {
      return uniqBy(
        this.areas.filterByCityId(cityId)
          .onlyDistrictsOrMoreDetailed
          .areas
          .map(a => this.facets.districts.find(d => d.code === String(a.district_id)))
          .filter(Boolean),
        'code',
      )
    },

    update(optionId) {
      const [level, code] = optionId.split(':')
      switch (level) {
        case 'region':
          this.updateRegion(Number(code))
          break
        case 'country':
          this.updateCountry(Number(code))
          break
        case 'city':
          this.updateCity(Number(code))
          break
        case 'district':
          this.updateDistrict(Number(code))
          break
      }
    },

    updateRegion(id) {
      this.$emit('selected', this.condition.update({
        [SearchCondition.PARAMS[`REGION_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: id,
        [SearchCondition.PARAMS[`COUNTRY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
        [SearchCondition.PARAMS[`CITY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
        [SearchCondition.PARAMS[`DISTRICT_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
      }))
    },
    updateCountry(id) {
      const country = this.areas.findCountryById(id)
      if (!country) return

      this.$emit('selected', this.condition.update({
        [SearchCondition.PARAMS[`REGION_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(country.region_id),
        [SearchCondition.PARAMS[`COUNTRY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: id,
        [SearchCondition.PARAMS[`CITY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
        [SearchCondition.PARAMS[`DISTRICT_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
      }))
    },
    updateCity(id) {
      const city = this.areas.findCityById(id)
      if (!city) return

      this.$emit('selected', this.condition.update({
        [SearchCondition.PARAMS[`REGION_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(city.region_id),
        [SearchCondition.PARAMS[`COUNTRY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(city.country_id),
        [SearchCondition.PARAMS[`CITY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: id,
        [SearchCondition.PARAMS[`DISTRICT_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: null,
      }))
    },
    updateDistrict(id) {
      const district = this.areas.findDistrictById(id)
      if (!district) return

      this.$emit('selected', this.condition.update({
        [SearchCondition.PARAMS[`REGION_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(district.region_id),
        [SearchCondition.PARAMS[`COUNTRY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(district.country_id),
        [SearchCondition.PARAMS[`CITY_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: asNumber(district.city_id),
        [SearchCondition.PARAMS[`DISTRICT_OF_DESTINATION${this.selectingDestinationIndex + 1}`]]: id,
      }))
    },
    closeModal() {
      this.isModalOpened = false
      this.keyword = ''
      this.autocompleteItems = []
    },
    selectAutocompleteItem(optionId) {
      if (!optionId) {
        this.keyword = ''
        this.autocompleteItems = []
      } else {
        this.update(optionId)
        this.closeModal()
      }
    },
  },
}
</script>

<style lang="scss">
// モーダルコンポーネントが親からスタイルを指定する仕様のため、やむなく scoped を外している
.destinationPicker_modal {
  .modal-content {
    background: none;
  }

  &.modal-sp-fullscreen {
    background: none;
  }
}

.destinationPicker_tabs {
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 100%;
  padding: 0 5px;

  & li {
    position: relative;
    justify-content: center;
    top: 10px;
    float: left;
    width: calc(50% - 2px);
    height: 35px;
    padding: 10px 0;
    background-color: #f9423a;
    color: white;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    border: 1px solid #f9423a;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    cursor: pointer;

    &.active {
      top: 1px;
      height: 45px;
      background-color: #ffffff;
      color: #f9423a;
      border: 1px solid #ffffff;
      border-bottom: none;
    }
  }
}
</style>

<style scoped lang="scss">
.destinationPicker_modalHeader {
  position: relative;
  width: 100%;
  margin: auto;
  padding: 15px 0;
  background: #f9423a url(https://www.tabikobo.com/images/new2016/icon_select_area_w.png) no-repeat scroll 2% center / 20px auto;
  color: #fff;
  font-size: 14px;
  font-family: "ヒラギノ角ゴ Pro W3", "Hiragino Kaku Gothic Pro", "メイリオ", Meiryo, "ＭＳ Ｐゴシック", "MS PGothic", Osaka, sans-serif;
  text-align: center;
}

.destinationPicker_modalHeaderCrossMarkArea {
  display: table-cell;
  position: absolute;
  top: 20px;
  right: 0;
  float: none;
  width: 40px;
  height: 100%;
  background: none;
  border-radius: 0;
  cursor: pointer;
}

.destinationPicker_noDestinations {
  width: 299px;
  padding: 6px 20px 8px;
  background-color: #fff;
  color: #f9423a;
  font-size: 14px;
  font-weight: bold;

  @media (max-width: 657px) {
    width: 188px;
  }
}

.destinationPicker_goBackLevelArea {
  display: flex;
  width: 100%;
  letter-spacing: -.4em;
}

.destinationPicker_goBackLevelItem {
  display: table;
  position: relative;
  float: left;
  width: 100%;
  height: 45px;
  background-color: #fff;
  color: #5b2e23;
  font-size: 14px;
  letter-spacing: normal;
  border-top: 0 none;
  border-bottom: 1px solid #dcdcdc;
  border-right: 1px solid #dcdcdc;
}

.destinationPicker_goBackIconTouchableArea {
  position: absolute;
  z-index: 1;
  width: 50px;
  height: 100%;
  cursor: pointer;
}

.destinationPicker_goBackLevelLabel {
  display: table-cell;
  padding-left: 12px;
  padding-right: 36px;
  text-align: center;
  font-family: "ヒラギノ角ゴ Pro W3", "Hiragino Kaku Gothic Pro", "メイリオ", Meiryo, "ＭＳ Ｐゴシック", "MS PGothic", Osaka, sans-serif;
  vertical-align: middle;
}

.destinationPicker_goBackLevelArrow {
  position: absolute;
  top: 50%;
  left: 20px;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);

  &::before {
    box-sizing: initial;
  }

  &::after {
    box-sizing: initial;
  }
}

.destinationPicker_autocomplete {
  width: calc(100% - 20px);
  margin: 20px 10px 40px;
}
</style>
